import {
  cardFromJson,
  cardToJson,
  type CardRecord,
  type ReviewRecord,
  type Snapshot,
} from './backend';
import { SCHEMA_VERSION } from './pocketbase';
import { t } from '../../strings';
import { SKILLS, type CardKey, type Skill } from '../../types';

/**
 * The JSON backup format — the one way progress moves between backends, and
 * the route for lifting a device's local progress up to a PocketBase account.
 * `Date` values are ISO strings here, so a backup taken from IndexedDB imports
 * into PocketBase unchanged.
 */
export interface BackupFile {
  app: 'syrinx';
  schemaVersion: number;
  exportedAt: string;
  cards: Record<string, unknown>[];
  reviews: Record<string, unknown>[];
  settings: Record<string, unknown>;
}

export function snapshotToJson(snap: Snapshot, now: Date = new Date()): BackupFile {
  return {
    app: 'syrinx',
    schemaVersion: SCHEMA_VERSION,
    exportedAt: now.toISOString(),
    cards: snap.cards.map((c) => ({
      key: c.key,
      code: c.code,
      skill: c.skill,
      card: cardToJson(c.card),
    })),
    reviews: snap.reviews.map((r) => ({
      key: r.key,
      rating: r.rating,
      correct: r.correct,
      // Written only when there was a pick, so a log full of flip-card grades
      // doesn't carry a column of empty strings.
      ...(r.picked ? { picked: r.picked } : {}),
      ts: r.ts,
    })),
    settings: snap.settings,
  };
}

export class BackupFormatError extends Error {}

/**
 * The oldest backup this build can still read.
 *
 * Every schema bump so far has been additive to the backup format — v2 added
 * `picked` to a review — so an older file imports cleanly, just without the
 * fields that did not exist when it was written. Refusing it would strand every
 * backup anyone has already taken, which is the one thing a backup must not do.
 * A bump that genuinely changes the shape of a record raises this constant and
 * says so here.
 */
export const MIN_READABLE_SCHEMA = 1;

/** Parse and validate a backup file. Throws BackupFormatError on anything odd. */
export function snapshotFromJson(raw: unknown): Snapshot {
  const f = (raw ?? {}) as Record<string, unknown>;
  if (f['app'] !== 'syrinx') {
    throw new BackupFormatError(t('app.errors.backupNotSyrinx'));
  }
  const version = f['schemaVersion'];
  if (
    typeof version !== 'number' ||
    version < MIN_READABLE_SCHEMA ||
    version > SCHEMA_VERSION
  ) {
    throw new BackupFormatError(
      t('app.errors.backupSchema', {
        version: String(version),
        min: MIN_READABLE_SCHEMA,
        max: SCHEMA_VERSION,
      }),
    );
  }

  const cards: CardRecord[] = [];
  for (const entry of asArray(f['cards'], 'cards')) {
    const r = entry as Record<string, unknown>;
    const skill = r['skill'] as Skill;
    if (typeof r['key'] !== 'string' || !SKILLS.includes(skill)) {
      throw new BackupFormatError(t('app.errors.backupCardKey'));
    }
    const card = cardFromJson(r['card']);
    if (Number.isNaN(card.due.getTime())) {
      throw new BackupFormatError(t('app.errors.backupCardDue', { key: String(r['key']) }));
    }
    cards.push({
      key: r['key'] as CardKey,
      code: String(r['code'] ?? ''),
      skill,
      card,
      due: card.due,
    });
  }

  const reviews: ReviewRecord[] = [];
  for (const entry of asArray(f['reviews'], 'reviews')) {
    const r = entry as Record<string, unknown>;
    if (typeof r['key'] !== 'string' || typeof r['ts'] !== 'number') {
      throw new BackupFormatError(t('app.errors.backupReviewKey'));
    }
    // Ids are backend-local; import always assigns fresh ones.
    const picked = typeof r['picked'] === 'string' ? r['picked'] : '';
    reviews.push({
      id: '',
      key: r['key'] as CardKey,
      rating: Number(r['rating'] ?? 0),
      correct: r['correct'] === true,
      ...(picked ? { picked } : {}),
      ts: r['ts'],
    });
  }

  const settings = f['settings'];
  if (settings !== undefined && (typeof settings !== 'object' || settings === null || Array.isArray(settings))) {
    throw new BackupFormatError(t('app.errors.backupSettings'));
  }

  return { cards, reviews, settings: { ...((settings ?? {}) as Record<string, unknown>) } };
}

function asArray(v: unknown, what: string): unknown[] {
  if (v === undefined) return [];
  if (!Array.isArray(v)) throw new BackupFormatError(t('app.errors.backupNotList', { section: what }));
  return v;
}
